/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import React, { useState, useEffect } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { Button, Loading, TooltipDefinition } from 'carbon-addons-iot-react';
import Add from '@carbon/icons-react/lib/add/20';
import Subtract from '@carbon/icons-react/lib/subtract/20';
import { useField } from 'formik';
import hash from 'object-hash';

import ValidationOptionsModal from './ValidationOptionsModal';
import * as utils from './schemaEditorUtils';
import SchemaDatatypeInput from './SchemaDatatypeInput';
import SchemaDefaultInput from './SchemaDefaultInput';

import {
  SourceViewSwitcher,
  TextInputFormik,
  ToggleSmallFormik,
  JsonEditorFormik,
  JsonSchemas,
  SimpleCommonTable,
  TableEmptyState,
} from 'Common';

const messages = defineMessages({
  property: 'Property',
  dataType: 'Data Type',
  defaultValue: 'Default Value',
  required: 'Required',
  addNewProperty: 'Add new property',
  validation: 'Validation',
  placeholder: 'Placeholder',
  resource: 'Resource',
  content: 'Content',

  addProperty: 'Add property',
  addChildProperty: 'Add child',
  removeProperty: 'Remove',

  schemaName: 'Name',
  schemaDescription: 'Description',
  schemaNamePlaceholder: 'Schema name',
  schemaDescriptionPlaceholder: 'Schema description',

  noProperties: 'The schema does not define any properties yet',
  schemaMustDefineProperties: 'The schema must define at least one property',

  propertyNamePlaceholder: 'Enter a name for the property',
  propertyDefaultPlaceholder: 'Enter a default value for the property',
  propertyTypePlaceholder: 'Select type',

  arrayType: 'Array type',
  arrayTypeTooltip: 'Use this row to define the type of item that this array can contain',

  dropChildrenModalHeading: 'Change data type?',
  dropChildrenModalAriaLabel: 'change data type confirmation modal',
  dropChildrenModalContentText:
    'Changing the data type will remove all child properties and/or array item type definitions. Continue?',
  dropChildrenModalCancelActionText: 'Cancel',
  dropChildrenModalConfirmActionText: 'Continue',
});

const mapToTableData = (content, formatMessage, readOnly, path) => {
  const node = get(content, path);

  if (isNil(node)) {
    return undefined;
  }

  // TODO: need to also check schema node type includes (or equals) "object"? E.g. || utils.isOrIncludesObject(node.type)
  // For now, just go off the presence of "properties"
  if (isArray(node.properties)) {
    return node.properties.reduce((acc, v, i) => {
      const childPath = `${path}.properties[${i}]`;
      const childNode = get(content, childPath);
      return [
        ...acc,
        {
          id: childPath,
          values: {},
          rowActions: [
            // + shown for object (properties only
            // check datatype is either just "object", or an array that includes "object"
            ...(utils.ensureArray(childNode.type).includes(utils.JSON_SCHEMA_DATATYPES.object)
              ? [
                  {
                    labelText: formatMessage(messages.addChildProperty),
                    id: utils.ROW_ACTION_IDS.ADD_PROPERTY,
                    isOverflow: true,
                    renderIcon: Add,
                    disabled: readOnly,
                  },
                ]
              : []),

            {
              labelText: formatMessage(messages.removeProperty),
              id: utils.ROW_ACTION_IDS.REMOVE_PROPERTY,
              isOverflow: true,
              renderIcon: Subtract,
              disabled: readOnly,
            },
            {
              labelText: formatMessage(messages.validation),
              id: utils.ROW_ACTION_IDS.OPEN_VALIDATION_OPTIONS_MODAL,
              isOverflow: false,
              disabled: false, // can still be opened for viewing even when not in edit mode
            },
          ],
          children: mapToTableData(content, formatMessage, readOnly, childPath),
        },
      ];
    }, []);
  }

  // TODO: need to also check schema node type includes (or equals) "array"? E.g. || utils.isOrIncludesArray(node.type)
  // For now, just go off the presence of "properties"
  if (isPlainObject(node.items)) {
    // TODO: need to handle item arrays? (i.e. polymorphic arrays?)

    const childPath = `${path}.items`;
    const childNode = get(content, childPath);
    return [
      {
        id: childPath,
        values: {
          isItemsRow: true, // <-- this instructs the renderDataFunctions to appropriate components for this special type of row
        },
        rowActions: [
          // + shown for object (properties only
          // check datatype is either just "object", or an array that includes "object"
          ...(utils.ensureArray(childNode.type).includes(utils.JSON_SCHEMA_DATATYPES.object)
            ? [
                {
                  labelText: formatMessage(messages.addChildProperty),
                  id: utils.ROW_ACTION_IDS.ADD_PROPERTY,
                  isOverflow: true,
                  renderIcon: Add,
                  disabled: readOnly,
                },
              ]
            : []),

          {
            labelText: formatMessage(messages.validation),
            id: utils.ROW_ACTION_IDS.OPEN_VALIDATION_OPTIONS_MODAL,
            isOverflow: false,
            disabled: false, // can still be opened for viewing even when not in edit mode
          },
        ],
        children: mapToTableData(content, formatMessage, readOnly, childPath),
      },
    ];
  }

  return [];
};

const propTypes = {
  formikNamespace: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  backgroundColor: PropTypes.string,
  title: PropTypes.string,
};

const defaultProps = {
  readOnly: true,
  title: '',
  backgroundColor: '',
};

const withNamespace = (namespace, fieldName) =>
  namespace ? `${namespace}.${fieldName}` : fieldName;

const SchemaEditor = ({
  formikNamespace,
  readOnly,
  backgroundColor,
  title,
  intl: { formatMessage },
}) => {
  const [inSourceView, setInSourceView] = useState(false);

  const [field, meta, helpers] = useField(formikNamespace);

  const nameFieldId = withNamespace(formikNamespace, 'name');
  const contentFieldId = withNamespace(formikNamespace, 'content');

  const [nameField] = useField(nameFieldId);
  const [contentField] = useField(contentFieldId);

  // true/false indicates whether or not the source entered by the user was successfully
  // transformed and synced to a formik model
  // null indicates we're waiting for the value debounce timeout to expire (and transformation/syncing to complete)
  const [sourceViewParseErrorStatus, setSourceViewParseErrorStatus] = useState(false);

  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const [tableData, setTableData] = useState(null);

  const [tableDataKey, setTableDataKey] = useState(uuidv4());

  // NOTE: description field is not required so we don't factor this into loading status
  // (this is OK, it is sourced from the same resource as name under the covers)
  const isLoading =
    nameField.value === undefined || contentField.value === undefined || contentField.value === '';

  // calling this prompts table data to be recomputed
  // must be set to true whenever an operation is performed that could
  // influence something in the table data structure - rows, children, row actions (e.g. add/remove property, change row datatype)
  // DOES NOT need to be called when just updating a value in a row that does not influuence table structure (e.g. property names, default value, required)
  const invalidateTableData = () => {
    setTableDataKey(uuidv4());
  };

  useEffect(() => {
    const newTableData = mapToTableData(field.value, formatMessage, readOnly, 'content');

    // sync the data table
    setTableData(newTableData);

    // recompute table data iff
    // 1. we switch back from source view (so any changes to the source are reflected in the table)
    // 2. we've explicitly invalidated table data (i.e. added/removed/changed datatype of a property)
  }, [tableDataKey, hash(field.value)]); /* eslint-disable-line react-hooks/exhaustive-deps */

  // invalidate table data if formik namespace changes
  //  (i.e. we're rendering a different schema, because e.g. we've switched to different event ID in the PI editor)
  // invalidate table data when loading status changes
  // invalidate table data when readOnly status changes (some row actions might need to have their disabled status updated)
  useEffect(() => {
    invalidateTableData();
  }, [formikNamespace, isLoading, readOnly]);

  // invalidate table data when we switch back from sourceView
  // (may have changed structure of the table in the source editor)
  useEffect(() => {
    if (!inSourceView) {
      invalidateTableData();
    }
  }, [inSourceView]);

  const [validationOptionsModalFieldId, setValidationOptionsModalFieldId] = useState(undefined);
  const [validationOptionsModalOpen, setValidationOptionsModalOpen] = useState(false);
  const onOpenValidationOptionsModal = rowId => {
    setValidationOptionsModalFieldId(`${formikNamespace}.${rowId}`);
    setValidationOptionsModalOpen(true);
  };
  const onCloseValidationOptionsModal = () => {
    setValidationOptionsModalOpen(false);
  };

  const onAddProperty = rowId => {
    if (rowId === undefined) {
      return;
    }

    let cloned;
    if (isNil(field.value) || field.value === '') {
      // no content yet, inialise a blank schema with a single new property
      cloned = {
        $schema: 'http://json-schema.org/draft-04/schema#',
        type: 'object',
        additionalProperties: false,
      };
    } else {
      cloned = cloneDeep(field.value);
    }

    const node = get(cloned, rowId);
    if (isNil(node.properties)) {
      node.properties = [];
    }

    node.properties.push({
      name: '',
      type: undefined,
      required: false,
      allowNull: false,
    });
    helpers.setValue(cloned);
    invalidateTableData();
  };

  const onRemoveProperty = rowId => {
    // rowId e.g.: content.properties[3].items.items.properties[0]
    // parentPropertiesNodePath = content.properties[3].items.items.properties
    // indexInParentPropertiesNode = 0

    const parts = /(.+)\[(\d+)\]/.exec(rowId);
    const parentPropertiesNodePath = parts[1];
    const indexInParentPropertiesNode = parts[2];

    const cloned = cloneDeep(field.value);
    const parentPropertiesNode = get(cloned, parentPropertiesNodePath);
    parentPropertiesNode.splice(indexInParentPropertiesNode, 1);
    helpers.setValue(cloned);
    invalidateTableData();
  };

  return (
    <>
      {validationOptionsModalOpen ? (
        <ValidationOptionsModal
          fieldId={validationOptionsModalFieldId}
          open
          readOnly={readOnly}
          onRequestClose={onCloseValidationOptionsModal}
        />
      ) : null}
      <div className="schema-editor">
        {isLoading ? (
          <div className="schema-editor--loading">
            <Loading active small={false} withOverlay={false} />
          </div>
        ) : (
          <>
            <div className="schema-editor--header">
              <h5>{title}</h5>
              <div>
                <SourceViewSwitcher
                  sourceHasErrors={sourceViewParseErrorStatus}
                  inSourceView={inSourceView}
                  onChange={newInSourceView => setInSourceView(newInSourceView)}
                />
              </div>
            </div>

            <div className={`schema-editor--content ${backgroundColor === 'gray' ? 'gray' : ''}`}>
              {inSourceView ? (
                <div className="schema-editor--content--sourceview">
                  <JsonEditorFormik
                    fieldId={contentFieldId}
                    options={{
                      readOnly,
                    }}
                    formikModelToJson={model => utils.untransformContent(model)}
                    jsonToFormikModel={json => utils.transformContent(json)}
                    onChangeParseErrorStatus={setSourceViewParseErrorStatus}
                    schema={JsonSchemas.WIOTP_IM_SCHEMA_CONTENT}
                  />
                </div>
              ) : (
                <SimpleCommonTable
                  id="schema-editor--content--table"
                  className="schema-editor--content--table"
                  useZebraStyles={false}
                  columns={[
                    /* eslint-disable react/destructuring-assignment */
                    {
                      id: 'property',
                      name: formatMessage(messages.property),
                      isSortable: false,
                      renderDataFunction: params =>
                        get(params, 'row.isItemsRow') ? (
                          <div
                            key={`${formikNamespace}.${params.rowId}.name`}
                            className="schema-editor--content--table--tooltip">
                            <TooltipDefinition
                              tooltipText={formatMessage(messages.arrayTypeTooltip)}
                              align="right"
                              direction="bottom">
                              {formatMessage(messages.arrayType)}
                            </TooltipDefinition>
                          </div>
                        ) : (
                          <TextInputFormik
                            key={`${formikNamespace}.${params.rowId}.name`}
                            id={`${formikNamespace}.${params.rowId}.name`}
                            disabled={readOnly}
                            label=""
                            hideLabel
                            placeholder={formatMessage(messages.propertyNamePlaceholder)}
                          />
                        ),
                    },

                    {
                      id: 'dataType',
                      name: formatMessage(messages.dataType),
                      isSortable: false,
                      renderDataFunction: params => (
                        <SchemaDatatypeInput
                          key={`${formikNamespace}.${params.rowId}.type`}
                          id={`${formikNamespace}.${params.rowId}`}
                          readOnly={readOnly}
                          // need to recompute table data when datatype for a row changes
                          // (since this can influence which row actions need to be shown, and what children the row might have)
                          onChange={type__ => invalidateTableData()}
                          placeholder={formatMessage(messages.propertyTypePlaceholder)}
                          modal={{
                            heading: formatMessage(messages.dropChildrenModalHeading),
                            contentText: formatMessage(messages.dropChildrenModalContentText),
                            ariaLabel: formatMessage(messages.dropChildrenModalAriaLabel),
                            cancelActionText: formatMessage(
                              messages.dropChildrenModalCancelActionText
                            ),
                            confirmActionText: formatMessage(
                              messages.dropChildrenModalConfirmActionText
                            ),
                          }}
                        />
                      ),
                    },

                    {
                      id: 'defaultValue',
                      name: formatMessage(messages.defaultValue),
                      isSortable: false,
                      renderDataFunction: params => (
                        <SchemaDefaultInput
                          key={`${formikNamespace}.${params.rowId}.default`}
                          id={`${formikNamespace}.${params.rowId}`}
                          readOnly={readOnly}
                          placeholder={formatMessage(messages.propertyDefaultPlaceholder)}
                        />
                      ),
                    },
                    {
                      id: 'required',
                      name: formatMessage(messages.required),
                      isSortable: false,
                      width: '4rem',
                      align: 'center',
                      renderDataFunction: params =>
                        get(params, 'row.isItemsRow') ? undefined : (
                          <ToggleSmallFormik
                            key={`${formikNamespace}.${params.rowId}.reqiored`}
                            id={`${formikNamespace}.${params.rowId}.required`}
                            disabled={readOnly}
                            aria-label="property required toggle"
                          />
                        ),
                    },
                    /* eslint-enable react/destructuring-assignment */
                  ]}
                  data={tableData || []}
                  view={{
                    table: {
                      expandedIds: expandedRowIds,
                      emptyState: (
                        <TableEmptyState
                          message={formatMessage(
                            meta.touched
                              ? messages.schemaMustDefineProperties
                              : messages.noProperties
                          )}
                        />
                      ),
                    },
                    toolbar: !readOnly
                      ? {
                          customToolbarContent: (
                            <Button
                              kind="secondary"
                              onClick={() => onAddProperty('content')}
                              disabled={readOnly}>
                              {formatMessage(messages.addProperty)}
                            </Button>
                          ),
                        }
                      : undefined,
                  }}
                  options={{
                    hasPagination: false,
                    hasFilter: false,
                    hasRowActions: true,
                    hasSearch: false,
                    hasColumnSelection: false,
                    hasRowSelection: false,
                    hasOnlyPageData: true,
                    shouldExpandOnRowClick: false,
                    hasRowExpansion: true,
                    hasRowNesting: true,
                  }}
                  actions={{
                    pagination: {
                      onChangePage: noop,
                    },
                    table: {
                      onApplyRowAction: (actionId, rowId) => {
                        switch (actionId) {
                          case utils.ROW_ACTION_IDS.REMOVE_PROPERTY:
                            onRemoveProperty(rowId);
                            break;

                          case utils.ROW_ACTION_IDS.ADD_PROPERTY:
                            onAddProperty(rowId);
                            break;

                          case utils.ROW_ACTION_IDS.OPEN_VALIDATION_OPTIONS_MODAL:
                            onOpenValidationOptionsModal(rowId);
                            break;

                          default:
                            break;
                        }
                      },
                      onChangeOrdering: noop,
                      onChangeSort: noop,
                      onColumnResize: noop,
                      onColumnSelectionConfig: noop,
                      onEmptyStateAction: () => onAddProperty('content'),
                      onRowClicked: noop,
                      onRowExpanded: (rowId, expanded) => {
                        if (expanded) {
                          setExpandedRowIds([...expandedRowIds, rowId]);
                        } else {
                          setExpandedRowIds(expandedRowIds.filter(i => i !== rowId));
                        }
                      },
                      onRowSelected: noop,
                      onSelectAll: noop,
                    },
                    toolbar: {
                      onApplyBatchAction: noop,
                      onApplyFilter: noop,
                      onApplySearch: noop,
                      onCancelBatchAction: noop,
                      onClearAllFilters: noop,
                      onToggleColumnSelection: noop,
                      onToggleFilter: noop,
                    },
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

SchemaEditor.propTypes = propTypes;
SchemaEditor.defaultProps = defaultProps;
export default injectIntl(SchemaEditor);
